"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";

const BE = process.env.NEXT_PUBLIC_BE ?? "";
const CACHE_KEY = "lcr:admin-status";

type AdminStatus = {
	isAdmin: boolean;
	displayName?: string;
	adminUrl?: string;
	editUrlBase?: string;
};

export default function AdminBar() {
	const pathname = usePathname();
	const [status, setStatus] = useState<AdminStatus | null>(null);
	const [wpId, setWpId] = useState<string | null>(null);

	// Re-read on navigation: the id comes from the newly rendered page's metadata.
	useEffect(() => {
		const meta = document.querySelector('meta[name="lcr:wp-id"]');
		setWpId(meta?.getAttribute("content") ?? null);
	}, [pathname]);

	useEffect(() => {
		try {
			const cached = sessionStorage.getItem(CACHE_KEY);
			if (cached) {
				setStatus(JSON.parse(cached) as AdminStatus);
				return;
			}
		} catch {
			// sessionStorage unavailable; fall through to the network check.
		}

		let active = true;

		fetch(`${BE}/wp-json/lcr/v1/admin-status`, {
			credentials: "include",
			headers: { Accept: "application/json" },
		})
			.then((res) => (res.ok ? res.json() : null))
			.then((data: AdminStatus | null) => {
				if (!active || !data) return;
				try {
					sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));
				} catch {
					// Non-fatal: the status just won't be cached for this session.
				}
				setStatus(data);
			})
			.catch(() => {
				// Not logged in, offline, or blocked: the bar simply stays hidden.
			});

		return () => {
			active = false;
		};
	}, []);

	if (!status?.isAdmin) {
		return null;
	}

	const editUrl =
		wpId && status.editUrlBase
			? `${status.editUrlBase}?post=${wpId}&action=edit`
			: null;

	return (
		<div className="fixed bottom-4 left-4 z-[9999] flex items-center gap-2 rounded-lg bg-neutral-900/95 px-3 py-2 text-sm text-white shadow-lg">
			<span className="hidden pr-1 font-medium sm:inline">
				{status.displayName ? `Hi, ${status.displayName}` : "WordPress"}
			</span>

			{editUrl ? (
				<a
					className="rounded bg-white px-2.5 py-1 font-medium text-neutral-900 hover:bg-neutral-200"
					href={editUrl}
					rel="noreferrer"
					target="_blank"
				>
					Edit this page
				</a>
			) : (
				<span className="px-2.5 py-1 text-neutral-400">Not editable</span>
			)}

			<a
				className="rounded px-2.5 py-1 hover:bg-white/10"
				href={status.adminUrl}
				rel="noreferrer"
				target="_blank"
			>
				Dashboard
			</a>
		</div>
	);
}
